import React, { useState } from 'react';
import { FiPlus, FiHash, FiSearch, FiCheck, FiClock } from 'react-icons/fi';
import { useAuth } from '../../AuthContext';
import API_URL from '../../config';

const WelcomeCard = () => {
  const { currentUser, fetchUserProfile, setCurrentSociety } = useAuth();
  const [mode, setMode] = useState(null); // null | 'create' | 'join' | 'search'
  const [societyName, setSocietyName] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [requestedIds, setRequestedIds] = useState(new Set());

  const handleSearch = async (e) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (q.length < 3) return;
    setSearching(true);
    setHasSearched(true);
    setSearchResults([]);
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch(`${API_URL}/societies/search?q=${encodeURIComponent(q)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSearchResults(data);
        const alreadyRequested = new Set();
        data.forEach(s => { if (s.has_pending_request) alreadyRequested.add(s.id); });
        setRequestedIds(prev => new Set([...prev, ...alreadyRequested]));
      }
    } catch {
      // Silently fail search
    }
    setSearching(false);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!societyName.trim()) return;
    setLoading(true);
    setError('');
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch(`${API_URL}/auth/setup-society`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ name: societyName.trim() }),
      });
      const data = await res.json();
      if (res.ok) {
        const updated = await fetchUserProfile(currentUser);
        if (data.society) {
          setCurrentSociety(data.society);
        } else if (updated?.societies?.length > 0) {
          setCurrentSociety(updated.societies[updated.societies.length - 1]);
        }
      } else {
        setError(data.error || 'Failed to create society.');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    }
    setLoading(false);
  };

  const handleJoin = async (e) => {
    e.preventDefault();
    if (!inviteCode.trim()) return;
    setLoading(true);
    setError('');
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch(`${API_URL}/auth/join-society`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ invite_code: inviteCode.trim() }),
      });
      const data = await res.json();
      if (res.ok || data.error === 'You are already a member of this society') {
        const updated = await fetchUserProfile(currentUser);
        if (updated?.societies) {
          const joined = updated.societies.find(
            (s) => s.invite_code === inviteCode.trim().toUpperCase()
          );
          if (joined) setCurrentSociety(joined);
        }
      } else {
        setError(data.error || 'Failed to join society.');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    }
    setLoading(false);
  };

  const handleRequestJoin = async (societyId) => {
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch(`${API_URL}/societies/${societyId}/request-join`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await res.json();
      if (res.ok) {
        setRequestedIds(prev => new Set([...prev, societyId]));
      } else if (data.error === 'You are already a member of this society') {
        // Refresh profile to pick up existing membership
        await fetchUserProfile(currentUser);
      } else {
        setError(data.error || 'Failed to send request.');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    }
  };

  const resetMode = () => {
    setMode(null);
    setError('');
    setSearchQuery('');
    setSearchResults([]);
  };

  return (
    <div className="welcome-card">
      <h2 className="welcome-title">Welcome to 1-19</h2>
      <p className="welcome-subtitle">
        Join your golf society to get started.
      </p>

      {error && <div className="welcome-error">{error}</div>}

      {mode === null && (
        <div className="welcome-actions">
          <button className="welcome-btn welcome-btn-secondary" onClick={() => setMode('join')}>
            <FiHash size={18} />
            I have an invite code
          </button>
          <button className="welcome-btn welcome-btn-primary" onClick={() => setMode('search')}>
            <FiSearch size={18} />
            Find my society
          </button>
          <button className="welcome-btn welcome-btn-ghost" onClick={() => setMode('create')}>
            <FiPlus size={18} />
            Start a new society
          </button>
        </div>
      )}

      {mode === 'join' && (
        <form className="welcome-form" onSubmit={handleJoin}>
          <input
            type="text"
            className="welcome-input"
            placeholder="Enter invite code"
            value={inviteCode}
            onChange={(e) => setInviteCode(e.target.value)}
            autoFocus
            required
          />
          <div className="welcome-form-actions">
            <button type="button" className="welcome-btn welcome-btn-ghost" onClick={resetMode} disabled={loading}>
              Back
            </button>
            <button type="submit" className="welcome-btn welcome-btn-primary" disabled={loading}>
              {loading ? 'Joining...' : 'Join'}
            </button>
          </div>
        </form>
      )}

      {mode === 'search' && (
        <div className="welcome-form">
          <form className="welcome-search-form" onSubmit={handleSearch}>
            <input
              type="search"
              className="welcome-input"
              placeholder="Society or organiser name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoFocus
            />
            <button
              type="submit"
              className={`welcome-search-btn${searchQuery.trim().length >= 3 ? ' ready' : ''}`}
              disabled={searching || searchQuery.trim().length < 3}
            >
              <FiSearch size={18} />
            </button>
          </form>

          {searchResults.length > 0 && (
            <div className="welcome-search-results">
              {searchResults.map(society => (
                <div key={society.id} className="welcome-search-item">
                  <div className="welcome-search-info">
                    <span className="welcome-search-name">{society.name}</span>
                    <span className="welcome-search-admin">Organised by {society.admin_name}</span>
                  </div>
                  {society.is_member ? (
                    <span className="welcome-search-status joined"><FiCheck size={14} /> Joined</span>
                  ) : requestedIds.has(society.id) ? (
                    <span className="welcome-search-status pending"><FiClock size={14} /> Requested</span>
                  ) : (
                    <button
                      className="welcome-btn welcome-btn-request"
                      onClick={() => handleRequestJoin(society.id)}
                    >
                      Request
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {!searching && hasSearched && searchResults.length === 0 && (
            <p className="welcome-search-hint">No societies found. Try a different name or ask your organiser for an invite code.</p>
          )}

          <button type="button" className="welcome-btn welcome-btn-ghost" onClick={resetMode}>
            Back
          </button>
        </div>
      )}

      {mode === 'create' && (
        <form className="welcome-form" onSubmit={handleCreate}>
          <input
            type="text"
            className="welcome-input"
            placeholder="Society name"
            value={societyName}
            onChange={(e) => setSocietyName(e.target.value)}
            autoFocus
            required
          />
          <div className="welcome-form-actions">
            <button type="button" className="welcome-btn welcome-btn-ghost" onClick={resetMode} disabled={loading}>
              Back
            </button>
            <button type="submit" className="welcome-btn welcome-btn-primary" disabled={loading}>
              {loading ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default WelcomeCard;
